import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Search, X, Filter, ArrowUpDown } from 'lucide-react';
import { AgentCard } from '@/components/AgentCard';
import { MARKETPLACE } from '@/constants/testIds';
import { motion } from 'framer-motion';

const SORT_OPTIONS = [
  { id: 'featured', label: 'Featured first' },
  { id: 'name-asc', label: 'A \u2192 Z' },
  { id: 'name-desc', label: 'Z \u2192 A' },
  { id: 'category', label: 'By category' },
];

function sortAgents(items, sortId) {
  const arr = [...items];
  if (sortId === 'name-asc') return arr.sort((a, b) => a.name.localeCompare(b.name));
  if (sortId === 'name-desc') return arr.sort((a, b) => b.name.localeCompare(a.name));
  if (sortId === 'category') return arr.sort((a, b) => (a.category || '').localeCompare(b.category || '') || a.name.localeCompare(b.name));
  // featured (default): featured first, then A-Z
  return arr.sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return a.name.localeCompare(b.name);
  });
}

export default function Marketplace() {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('All');
  const [sort, setSort] = useState('featured');

  // Filtered list (respects category + search)
  const { data, isLoading } = useQuery({
    queryKey: ['agents', q, category],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (category && category !== 'All') params.set('category', category);
      const res = await api.get(`/agents?${params.toString()}`);
      return res.data;
    },
  });

  // Unfiltered list \u2014 used purely for category count chips.
  const { data: allAgents } = useQuery({
    queryKey: ['agents-all'],
    queryFn: async () => (await api.get('/agents')).data,
    staleTime: 60_000,
  });

  const sortedItems = useMemo(() => sortAgents(data?.items || [], sort), [data, sort]);
  const totalCount = data?.items?.length || 0;

  const categoryCounts = useMemo(() => {
    const counts = {};
    (allAgents?.items || []).forEach((a) => {
      counts[a.category] = (counts[a.category] || 0) + 1;
    });
    return counts;
  }, [allAgents]);

  const categories = useMemo(() => ['All', ...(data?.categories || allAgents?.categories || [])], [data, allAgents]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
      <div className="flex flex-col gap-3 mb-8">
        <span className="text-[10px] uppercase tracking-widest text-[#FF6B1A]">// marketplace</span>
        <h1 className="display text-3xl md:text-4xl font-semibold tracking-tight">Hire your next 100x agent.</h1>
        <p className="text-white/65 max-w-2xl text-sm md:text-base">
          Browse operator-grade agents across sales, marketing, support, ops, finance, and more.
          Every agent gives you <span className="text-[#39FF14] font-medium">2 free tries — no signup needed</span>. That&apos;s 36 wow-moments waiting.
        </p>
      </div>

      {/* Search + Sort row */}
      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <input
            data-testid={MARKETPLACE.searchInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, role, or use case…"
            className="w-full pl-9 pr-9 py-3 rounded-xl bg-[hsl(225,18%,8%)] hairline focus:outline-none focus:border-[#FF6B1A]/50 focus:shadow-[0_0_0_3px_rgba(255,107,26,0.18)] text-sm"
          />
          {q && (
            <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="relative">
          <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 pointer-events-none" />
          <select
            data-testid="marketplace-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="pl-9 pr-8 py-3 rounded-xl bg-[hsl(225,18%,8%)] hairline text-sm text-white/85 focus:outline-none focus:border-[#39FF14]/50 focus:shadow-[0_0_0_3px_rgba(57,255,20,0.15)] appearance-none min-w-[180px]"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>Sort: {o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Category chips row */}
      <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-4 -mx-1 px-1">
        <div className="flex items-center gap-1.5 text-xs text-white/50 mr-2 shrink-0">
          <Filter className="h-3.5 w-3.5" /> Category
        </div>
        {categories.map((c) => {
          const count = c === 'All' ? (allAgents?.items?.length || 0) : (categoryCounts[c] || 0);
          return (
            <motion.button
              key={c}
              whileTap={{ scale: 0.96 }}
              data-testid={MARKETPLACE.categoryChip}
              onClick={() => setCategory(c)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs border transition-colors inline-flex items-center gap-1.5 ${
                category === c
                  ? 'border-[#39FF14]/60 text-[#39FF14] bg-[#39FF14]/8'
                  : 'border-border/60 text-white/70 hover:text-white hover:border-white/30'
              }`}
            >
              <span>{c}</span>
              <span className={`mono text-[10px] ${category === c ? 'text-[#39FF14]/75' : 'text-white/40'}`}>{count}</span>
            </motion.button>
          );
        })}
      </div>

      {/* Result count */}
      {!isLoading && (
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs text-white/55" data-testid="marketplace-result-count">
            {totalCount} agent{totalCount === 1 ? '' : 's'}
            {category !== 'All' && <> in <span className="text-white/85">{category}</span></>}
            {q && <> matching &ldquo;<span className="text-white/85">{q}</span>&rdquo;</>}
          </div>
          {(q || category !== 'All') && (
            <button
              onClick={() => { setQ(''); setCategory('All'); }}
              data-testid="marketplace-clear-filters"
              className="btn-ghost text-[11px] rounded-md px-2 py-1 text-white/55 hover:text-white"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`skel-${i}`} className="card-base p-5 h-56 animate-pulse" />
          ))}
        </div>
      ) : sortedItems.length === 0 ? (
        <div className="card-base p-8 text-center text-white/60">
          No agents match your search. Try clearing the filter.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedItems.map((a) => <AgentCard key={a.agent_id} agent={a} />)}
        </div>
      )}
    </div>
  );
}
